import type { ApiService } from '@/services/api'
import type { AppState } from '@/stores/app-state'
import { pdfCache } from '@/services/pdf-cache'

export class FileUpload {
  private container: HTMLElement
  private apiService: ApiService
  private appState: AppState
  private onUploadCallback: (fileId: string, filename: string) => Promise<void>
  private fileInput: HTMLInputElement | null = null

  constructor(
    container: HTMLElement,
    apiService: ApiService,
    appState: AppState,
    onUploadCallback: (fileId: string, filename: string) => Promise<void>
  ) {
    this.container = container
    this.apiService = apiService
    this.appState = appState
    this.onUploadCallback = onUploadCallback
    this.init()
  }

  private init(): void {
    this.render()
    this.setupEventListeners()
    console.log('📤 FileUpload initialized')
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="space-y-4">
        <div 
          id="drop-zone"
          class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
        >
          <div class="space-y-2">
            <div class="text-4xl">📄</div>
            <div class="text-lg font-medium text-gray-900">Select From Documents</div>
            <div class="text-sm text-gray-500">Maximum file size: 50MB</div>
          </div>
        </div>
        
        <input 
          type="file" 
          id="file-input" 
          accept="application/pdf,.pdf,image/*,.txt,.doc,.docx" 
          class="hidden"
        >
        
        <div id="upload-progress" class="hidden">
          <div class="bg-gray-200 rounded-full h-2">
            <div id="progress-bar" class="bg-blue-600 h-2 rounded-full transition-all duration-300" style="width: 0%"></div>
          </div>
          <div class="text-sm text-gray-600 mt-2">
            <span id="progress-text">Uploading...</span>
          </div>
        </div>
        
        <div id="upload-error" class="hidden text-red-600 text-sm"></div>
      </div>
    `

    this.fileInput = this.container.querySelector('#file-input')
  }

  private setupEventListeners(): void {
    const dropZone = this.container.querySelector('#drop-zone')
    const fileInput = this.fileInput

    if (!dropZone || !fileInput) return

    // Click to browse
    dropZone.addEventListener('click', () => {
      fileInput.click()
    })

    // File selection
    fileInput.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]
      if (file) {
        this.handleFileUpload(file)
      }
    })

    // Drag and drop
    dropZone.addEventListener('dragover', (event) => {
      event.preventDefault()
      dropZone.classList.add('border-blue-400', 'bg-blue-50')
    })

    dropZone.addEventListener('dragleave', () => {
      dropZone.classList.remove('border-blue-400', 'bg-blue-50')
    })

    dropZone.addEventListener('drop', (event) => {
      event.preventDefault()
      dropZone.classList.remove('border-blue-400', 'bg-blue-50')
      
      const dragEvent = event as DragEvent
      const files = dragEvent.dataTransfer?.files
      if (files && files.length > 0) {
        this.handleFileUpload(files[0])
      }
    })
  }

  private async handleFileUpload(file: File): Promise<void> {
    // Validate file type - support multiple document types
    const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    const isImage = file.type.startsWith('image/') || /\.(jpg|jpeg|png|gif|bmp|tiff|webp)$/i.test(file.name)
    const isText = file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt')
    const isWord = file.type.includes('word') || /\.(doc|docx)$/i.test(file.name)
    
    if (!isPDF && !isImage && !isText && !isWord) {
      this.showError('Please select a supported document file (PDF, image, text, or Word document)')
      return
    }

    // Validate file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      this.showError('File size exceeds 50MB limit')
      return
    }

    this.showProgress(true)
    this.hideError()

    let pdfArrayBuffer: ArrayBuffer | null = null

    try {
      // Read file as ArrayBuffer for caching
      this.updateProgress(0, 'Reading file...')
      pdfArrayBuffer = await this.readFileAsArrayBuffer(file)
      console.log(`📄 Read PDF file: ${file.name} (${(pdfArrayBuffer.byteLength / 1024 / 1024).toFixed(1)}MB)`)
      
      this.updateProgress(20, 'Uploading file...')

      // Get current project
      const currentProject = this.appState.getCurrentProject()
      if (!currentProject) {
        throw new Error('No project selected. Please select or create a project first.')
      }

      // Use unified document upload API
      const result = await this.apiService.uploadDocument(file, currentProject.id)
      
      this.updateProgress(80, 'Caching for fast access...')
      
      // Cache the document data for instant access later (only for PDFs)
      if (pdfArrayBuffer && isPDF) {
        pdfCache.cachePDF(result.file_id, pdfArrayBuffer, result.original_filename, currentProject.id)
        console.log(`📦 Cached uploaded document: ${result.original_filename}`)
      }
      
      this.updateProgress(90, 'Processing...')
      await new Promise(resolve => setTimeout(resolve, 300))
      
      this.updateProgress(100, 'Upload complete!')
      
      // Call the upload callback
      await this.onUploadCallback(result.file_id, result.original_filename)
      
      setTimeout(() => {
        this.showProgress(false)
      }, 1000)

    } catch (error) {
      console.error('Upload failed:', error)
      this.showError(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
      this.showProgress(false)
    }
  }

  /**
   * Read file as ArrayBuffer
   */
  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result)
        } else {
          reject(new Error('Failed to read file as ArrayBuffer'))
        }
      }
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'))
      }
      
      reader.readAsArrayBuffer(file)
    })
  }

  private showProgress(show: boolean): void {
    const progressEl = this.container.querySelector('#upload-progress')
    if (progressEl) {
      progressEl.classList.toggle('hidden', !show)
    }
  }

  private updateProgress(percent: number, text: string): void {
    const progressBar = this.container.querySelector('#progress-bar') as HTMLElement
    const progressText = this.container.querySelector('#progress-text')
    
    if (progressBar) {
      progressBar.style.width = `${percent}%`
    }
    
    if (progressText) {
      progressText.textContent = text
    }
  }

  private showError(message: string): void {
    const errorEl = this.container.querySelector('#upload-error')
    if (errorEl) {
      errorEl.textContent = message
      errorEl.classList.remove('hidden')
    }
  }

  private hideError(): void {
    const errorEl = this.container.querySelector('#upload-error')
    if (errorEl) {
      errorEl.classList.add('hidden')
    }
  }

  openFileDialog(): void {
    this.fileInput?.click()
  }
} 